import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, CheckCircle, AlertTriangle, Ban, Info, Lock, RefreshCw, Mail, Trash2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  normal: { bg: '', border: '', textColor: 'text-slate-600', icon: null },
  warning: { bg: 'bg-amber-50', border: 'border border-amber-200 rounded-xl p-6', textColor: 'text-amber-800', icon: AlertTriangle },
  success: { bg: 'bg-green-50', border: 'border border-green-200 rounded-xl p-6', textColor: 'text-green-700', icon: Shield },
  danger: { bg: 'bg-red-50', border: 'border border-red-200 rounded-xl p-6', textColor: 'text-red-700', icon: Ban },
  info: { bg: 'bg-emerald-50', border: 'border border-emerald-200 rounded-xl p-6', textColor: 'text-emerald-700', icon: Info }
};

const CONTACT_EMAIL = 'info@trustedtechno.com';

const PrivacyPolicy = () => {
  const { language, localize } = useLanguage();
  const t = (ar, en) => localize({ ar, en });
  const [sections, setSections] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const res = await axios.get(`${API}/privacy-policy`);
      if (res.data.sections && res.data.sections.length > 0) {
        setSections(res.data.sections);
        setLastUpdated(res.data.last_updated);
      }
    } catch {
      // fallback to empty
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;
      if (trimmed.startsWith('•') || trimmed.startsWith('✓')) {
        return (
          <li key={i} className="flex items-start gap-2 mb-1">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-60" />
            <span>{trimmed.substring(1).trim()}</span>
          </li>
        );
      }
      return <p key={i} className="mb-2">{trimmed}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" />
          {t('العودة للرئيسية', 'Back to Home')}
        </Link>

        <Card className="border-0 shadow-lg">
          <CardHeader className="text-center border-b pb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <Shield className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-3xl font-bold text-slate-900">
              {t('سياسة الخصوصية وحماية الأطفال', 'Privacy Policy & Child Protection')}
            </CardTitle>
            <p className="text-slate-500 mt-2">
              {lastUpdated
                ? `${t('آخر تحديث:', 'Last updated:')} ${new Date(lastUpdated).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', { year: 'numeric', month: 'long' })}`
                : t('آخر تحديث: مارس 2026', 'Last updated: March 2026')
              }
            </p>
          </CardHeader>

          <CardContent className="p-8 space-y-8" data-testid="privacy-content">
            {sections.length > 0 ? (
              sections.map((section, index) => {
                const style = TYPE_STYLES[section.type] || TYPE_STYLES.normal;
                const title = localize({ ar: section.title_ar, en: section.title_en });
                const content = localize({ ar: section.content_ar, en: section.content_en });
                const SectionIcon = style.icon;

                return (
                  <section key={section.id || index} data-testid={`privacy-section-${index}`}>
                    <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                      {SectionIcon && <SectionIcon className="w-5 h-5" />}
                      {index + 1}. {title}
                    </h2>
                    {style.border ? (
                      <div className={`${style.bg} ${style.border}`}>
                        <div className={`${style.textColor} leading-relaxed`}>
                          {renderContent(content)}
                        </div>
                      </div>
                    ) : (
                      <div className={`${style.textColor} leading-relaxed`}>
                        {renderContent(content)}
                      </div>
                    )}
                  </section>
                );
              })
            ) : (
              <section>
                <p className="text-slate-600 leading-relaxed">
                  {t(
                    'تلتزم منصة "تعلم مع ثقة" بحماية خصوصية جميع المستخدمين.',
                    'Learn With Trust is committed to protecting the privacy of all users.'
                  )}
                </p>
              </section>
            )}

            {/* AI Content Monitoring Disclosure */}
            <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-testid="ai-monitoring-section">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-500" />
                {t('فحص المحتوى بالذكاء الاصطناعي', 'AI Content Monitoring')}
              </h2>
              <div className="space-y-3 text-slate-600 text-sm leading-relaxed">
                <p>
                  {t(
                    'لضمان سلامة جميع المستخدمين وخاصة الأطفال، تستخدم المنصة نظام ذكاء اصطناعي لفحص المراسلات بين المستخدمين بشكل تلقائي. يهدف هذا النظام إلى رصد وحظر أي محتوى يتضمن إيذاء لفظي أو معنوي أو سلوك غير لائق.',
                    'To ensure the safety of all users, especially children, the platform uses an AI system to automatically scan communications between users. This system aims to detect and block any content that includes verbal or emotional abuse or inappropriate behavior.'
                  )}
                </p>
                <p>
                  {t(
                    'يتم الفحص بشكل آلي بالكامل دون تدخل بشري في قراءة محتوى الرسائل. في حال اكتشاف محتوى مخالف، يتم حظر الرسالة فوراً وإخطار إدارة المنصة.',
                    'Scanning is fully automated without human intervention in reading message content. If a violation is detected, the message is immediately blocked and platform administration is notified.'
                  )}
                </p>
              </div>
            </section>

            {/* Data Deletion Request Button */}
            <section className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="data-deletion-section">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                {t('طلب حذف البيانات', 'Request Data Deletion')}
              </h2>
              <p className="text-red-800 mb-4">
                {t(
                  'يحق لك طلب حذف جميع بياناتك في أي وقت. سيتم معالجة طلبك خلال 30 يومًا.',
                  'You have the right to request deletion of all your data at any time. Your request will be processed within 30 days.'
                )}
              </p>
              <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('طلب حذف بيانات', 'Data Deletion Request'))}&body=${encodeURIComponent(t('أرغب في طلب حذف جميع بياناتي من منصة تعلم مع ترست.\n\nالاسم: \nالبريد الإلكتروني المسجل: ', 'I would like to request deletion of all my data from Learn With Trust platform.\n\nName: \nRegistered Email: '))}`}>
                <Button className="bg-red-500 hover:bg-red-600 text-white" data-testid="request-deletion-btn">
                  <Mail className="w-4 h-4 ml-2" />
                  {t('طلب حذف البيانات', 'Request Data Deletion')}
                </Button>
              </a>
              <p className="text-xs text-red-600 mt-2">
                {t(`سيتم إرسال طلبك إلى ${CONTACT_EMAIL}`, `Your request will be sent to ${CONTACT_EMAIL}`)}
              </p>
            </section>

            {/* Contact Section */}
            <section className="bg-emerald-50 rounded-xl p-6 text-center" data-testid="contact-section">
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                {t('تواصل معنا', 'Contact Us')}
              </h2>
              <p className="text-slate-600 mb-4">
                {t('لأي استفسارات حول الخصوصية', 'For any privacy inquiries')}
              </p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-600 font-semibold text-lg hover:underline" data-testid="contact-email">
                {CONTACT_EMAIL}
              </a>
            </section>

            {/* Agreement */}
            <section className="border-t pt-6">
              <p className="text-center text-slate-500 text-sm">
                {t('باستخدامك لهذه المنصة، فإنك توافق على سياسة الخصوصية المذكورة أعلاه.', 'By using this platform, you agree to the privacy policy mentioned above.')}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
